import { Dictionary } from 'lodash';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Project } from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import { PageLoading } from 'components/atoms/loading';
import { ProjectFinancials } from 'components/organisms/projects/project-financials/financials';
import { RootState } from 'data';
import { useHasAccess } from 'hooks/use-has-access';
import { useFinancialFieldsQuery } from 'queries';
import { useAccountProfile } from 'queries/account';
import { useProjectFinancialsQuery } from 'queries/project';
import { FieldsStore } from 'stores/fields';
import { getFinancialsFieldIdFromLookupByName } from 'utils/project-financials';

interface Props {
  project: Project;
}

export const ProjectFinancialsPage: FC<Props> = ({ project }) => {
  const { data: account } = useAccountProfile();
  const { data: projectFinancials } = useProjectFinancialsQuery(
    project.projectId,
  );
  const { data: financialFieldsLookupByName } = useFinancialFieldsQuery();

  const fieldsStore = useSelector(({ fields: _fields }: RootState) => _fields);

  const profitabilityFieldId = getFinancialsFieldIdFromLookupByName(
    'costRate',
    financialFieldsLookupByName,
  );

  const { hasAccess: canAccessProfitability, isLoading: isPermissionsLoading } =
    useHasAccess({
      permissions: ['READ'],
      resource: 'projects',
      resourceId: project.projectId,
      resourceModule: 'fields',
      resourceModuleId: profitabilityFieldId,
      newProjectPermissionsApi: true,
    });

  if (
    account == null ||
    projectFinancials == null ||
    financialFieldsLookupByName == null ||
    isPermissionsLoading ||
    !FieldsStore.loaded(fieldsStore)
  ) {
    return <PageLoading css />;
  }

  const taskStatus = FieldsStore.getTaskStatusField(fieldsStore);

  const taskStatusOptionsLookup = (
    taskStatus[1]?.metaData.options ?? []
  ).reduce(
    (result, option) => ({
      ...result,
      [option.value]: option,
    }),
    {} as Dictionary<StatusFieldOption>,
  );

  return (
    <ProjectFinancials
      account={account}
      project={project}
      projectFinancials={projectFinancials}
      canAccessProfitability={canAccessProfitability}
      taskStatusOptionsLookup={taskStatusOptionsLookup}
    />
  );
};
